import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronRight, Activity, Cog, Users, BookOpen, Monitor, Award, Heart, Briefcase, Mail, ArrowUpRight, ShieldCheck, ScanSearch } from "lucide-react";
import { useState, useEffect, useRef, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import logoItac from "@/assets/logo-itac.webp";
import logoSecurefileMft from "@/assets/logo-securefile-mft-isotipo.webp";
import logoSecurefileAgent from "@/assets/logo-securefile-agent-isotipo.webp";
import logoWsGuardian from "@/assets/logo-wsguardian-isotipo.webp";
import logoAnkaSecure from "@/assets/logo-ankatech-isotipo-color.webp";
import logoAnkaSecureWhite from "@/assets/logo-ankatech-isotipo-white.webp";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ThemeContext } from "@/App";

type DropdownKey = "products" | "services" | "crypto" | "about" | null;

export function Navbar() {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<DropdownKey>(null);
  const [scrolled, setScrolled] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Determine if the navbar should be in "dark overlay" mode
  // On homepage before scrolling past hero → always dark (over the dark hero)
  // On homepage after hero OR on other pages → theme-dependent
  const isDarkNav = isHome && !pastHero ? true : theme === "dark";

  const ankaLogo = isDarkNav ? logoAnkaSecureWhite : logoAnkaSecure;
  const products = [
    { name: t("products.securefileMft.name"), href: "/productos/securefile-mft", icon: <img loading="lazy" decoding="async" width={28} height={28} src={logoSecurefileMft} alt="" className="w-7 h-7 object-contain" />, description: t("products.securefileMft.description") },
    { name: t("products.securefileAgent.name"), href: "/productos/securefile-agent", icon: <img loading="lazy" decoding="async" width={28} height={28} src={logoSecurefileAgent} alt="" className="w-7 h-7 object-contain" />, description: t("products.securefileAgent.description") },
    { name: t("products.wsGuardian.name"), href: "/productos/ws-guardian", icon: <img loading="lazy" decoding="async" width={28} height={28} src={logoWsGuardian} alt="" className="w-7 h-7 object-contain" />, description: t("products.wsGuardian.description") },
  ];

  const services = [
    { name: t("services.observabilidad.name"), href: "/observabilidad-monitoreo", icon: <Activity className="w-5 h-5" />, description: t("services.observabilidad.description") },
    { name: t("services.automationFactory.name"), href: "/fabrica-automatizacion", icon: <Cog className="w-5 h-5" />, description: t("services.automationFactory.description") },
  ];

  const crypto = [
    { name: t("crypto.gobiernoCriptografico.name"), href: "/journey-criptografico", icon: <ScanSearch className="w-5 h-5" />, description: t("crypto.gobiernoCriptografico.description") },
    { name: t("crypto.consultoriaPqc.name"), href: "/assessment-pasivo-criptografico", icon: <ShieldCheck className="w-5 h-5" />, description: t("crypto.consultoriaPqc.description") },
    { name: t("crypto.assessmentActivo.name"), href: "/assessment-activo-criptografico", icon: <Activity className="w-5 h-5" />, description: t("crypto.assessmentActivo.description") },
    { name: t("crypto.ankaSecure.name"), href: "/productos/ankasecure", icon: <img loading="lazy" decoding="async" width={28} height={28} src={ankaLogo} alt="" className="w-7 h-7 object-contain" />, description: t("crypto.ankaSecure.description") },
  ];

  const aboutLinks = [
    { name: t("nav.aboutMenu.whoWeAre"), href: "/nosotros/quienes-somos", icon: <Users className="w-5 h-5" />, description: t("nav.aboutMenu.whoWeAreDesc") },
    { name: t("nav.aboutMenu.blog"), href: "/nosotros/blog", icon: <BookOpen className="w-5 h-5" />, description: t("nav.aboutMenu.blogDesc") },
    { name: t("nav.aboutMenu.sessionTech"), href: "/nosotros/sessiontech", icon: <Monitor className="w-5 h-5" />, description: t("nav.aboutMenu.sessionTechDesc") },
    { name: t("nav.aboutMenu.sgcSgsi"), href: "/nosotros/sgc-sgsi", icon: <Award className="w-5 h-5" />, description: t("nav.aboutMenu.sgcSgsiDesc") },
    { name: t("nav.aboutMenu.rse"), href: "/nosotros/rse", icon: <Heart className="w-5 h-5" />, description: t("nav.aboutMenu.rseDesc") },
    { name: t("nav.aboutMenu.careers"), href: "/nosotros/trabaja-con-nosotros", icon: <Briefcase className="w-5 h-5" />, description: t("nav.aboutMenu.careersDesc") },
    { name: t("nav.aboutMenu.contact"), href: "/contacto", icon: <Mail className="w-5 h-5" />, description: t("nav.aboutMenu.contactDesc") },
  ];

  const navLinks = [
    { name: t("nav.softwareFactory"), href: "/fabrica-software" },
    { name: t("nav.partners"), href: "/partners" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      setPastHero(window.scrollY > window.innerHeight * 0.6);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const openDropdown = (key: DropdownKey) => {
    clearTimeout(timeoutRef.current);
    setActiveDropdown(key);
  };

  const closeDropdown = () => {
    timeoutRef.current = setTimeout(() => setActiveDropdown(null), 300);
  };

  const panelVariants = {
    hidden: { opacity: 0, y: 8, scale: 0.96 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] } },
    exit: { opacity: 0, y: 6, scale: 0.97, transition: { duration: 0.15 } },
  };

  // Text colors based on nav mode
  const textMain = isDarkNav ? "text-white/90" : "text-foreground";
  const textMuted = isDarkNav ? "text-white/70" : "text-muted-foreground";
  const textSubtle = isDarkNav ? "text-white/40" : "text-muted-foreground/60";
  const hoverText = isDarkNav ? "hover:text-white" : "hover:text-primary";
  const activeText = isDarkNav ? "text-white" : "text-primary";
  const pillBg = isDarkNav ? "bg-white/[0.04] border-white/[0.06]" : "bg-secondary/60 border-border";
  const iconBg = isDarkNav ? "bg-white/[0.06]" : "bg-primary/10";
  const iconColor = isDarkNav ? "text-[hsl(201,100%,60%)]" : "text-primary";
  const dividerColor = isDarkNav ? "bg-white/[0.06]" : "bg-border";

  const renderDropdownItem = (item: { name: string; href: string; icon: React.ReactNode; description: string; external?: boolean }, onClose?: () => void) => {
    const content = (
      <div className={cn(
        "group/item flex items-start gap-3 px-4 py-3 rounded-xl transition-all duration-200 cursor-pointer",
        isDarkNav ? "hover:bg-white/[0.06]" : "hover:bg-primary/5"
      )}>
        <div className={cn(
          "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover/item:scale-110 transition-transform duration-200",
          isDarkNav ? "bg-gradient-to-br from-[hsl(207,78%,50%)]/20 to-[hsl(27,86%,53%)]/10 text-[hsl(207,78%,60%)]" : "bg-primary/10 text-primary"
        )}>
          {item.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className={cn(
            "text-sm font-semibold transition-colors flex items-center gap-1.5",
            isDarkNav ? "text-white/90 group-hover/item:text-white" : "text-foreground group-hover/item:text-primary"
          )}>
            {item.name}
            {item.external && <ArrowUpRight className="w-3 h-3 opacity-40" />}
          </div>
          <div className={cn("text-xs mt-0.5 line-clamp-1", isDarkNav ? "text-white/40" : "text-muted-foreground")}>{item.description}</div>
        </div>
        <ChevronRight className={cn(
          "w-4 h-4 group-hover/item:translate-x-0.5 transition-all duration-200 mt-1 flex-shrink-0",
          isDarkNav ? "text-white/20 group-hover/item:text-white/50" : "text-muted-foreground/40 group-hover/item:text-primary/60"
        )} />
      </div>
    );

    if (item.external) {
      return (
        <a key={item.name} href={item.href} target="_blank" rel="noopener noreferrer" onClick={onClose}>
          {content}
        </a>
      );
    }
    return (
      <Link key={item.name} to={item.href} onClick={onClose}>
        {content}
      </Link>
    );
  };

  const NavButton = ({ label, dropdownKey }: { label: string; dropdownKey: DropdownKey }) => (
    <button
      onMouseEnter={() => openDropdown(dropdownKey)}
      onMouseLeave={closeDropdown}
      className={cn(
        "relative px-4 py-2 text-[13px] font-semibold tracking-wide uppercase transition-all duration-300",
        activeDropdown === dropdownKey ? activeText : cn(textMuted, hoverText)
      )}
    >
      {label}
      <motion.div
        className={cn("absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full", isDarkNav ? "bg-[hsl(201,100%,50%)]" : "bg-primary")}
        initial={false}
        animate={{ scale: activeDropdown === dropdownKey ? 1 : 0, opacity: activeDropdown === dropdownKey ? 1 : 0 }}
        transition={{ duration: 0.2 }}
      />
    </button>
  );

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        isDarkNav
          ? scrolled
            ? "bg-[hsl(207,90%,8%)]/90 backdrop-blur-2xl border-b border-white/[0.06] shadow-lg shadow-black/10"
            : "bg-[hsl(207,90%,8%)]"
          : scrolled
            ? "bg-background/90 backdrop-blur-2xl border-b border-border shadow-lg shadow-black/5"
            : "bg-background"
      )}
      onMouseLeave={closeDropdown}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center relative z-10">
            <img loading="eager" fetchPriority="high" decoding="async" width={1208} height={713}
              src={logoItac}
              alt="ITAC Logo"
              className={cn(
                "h-12 lg:h-14 w-auto object-contain transition-all duration-500 ease-out",
                isDarkNav && "brightness-0 invert",
                isHome && !pastHero
                  ? "opacity-0 -translate-y-3 scale-90 pointer-events-none"
                  : "opacity-100 translate-y-0 scale-100"
              )}
            />
          </Link>

          {/* Center nav pill */}
          <div className="hidden lg:flex items-center">
            <div className={cn("flex items-center gap-0 px-2 py-1.5 rounded-full backdrop-blur-sm", pillBg)}>
              <div className="relative" onMouseEnter={() => openDropdown("products")}>
                <NavButton label={t("nav.products")} dropdownKey="products" />
              </div>

              <Link
                to="/fabrica-software"
                className={cn("px-4 py-2 text-[13px] font-semibold tracking-wide uppercase transition-colors duration-300", textMuted, hoverText)}
                onMouseEnter={() => setActiveDropdown(null)}
              >
                {t("nav.softwareFactory")}
              </Link>

              <div className="relative" onMouseEnter={() => openDropdown("services")}>
                <NavButton label={t("services.label") || "Eficiencia Operativa"} dropdownKey="services" />
              </div>

              <div className="relative" onMouseEnter={() => openDropdown("crypto")}>
                <NavButton label={t("crypto.label") || "Gobierno Criptográfico"} dropdownKey="crypto" />
              </div>



              {navLinks.slice(1).map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={cn("px-4 py-2 text-[13px] font-semibold tracking-wide uppercase transition-colors duration-300", textMuted, hoverText)}
                  onMouseEnter={() => setActiveDropdown(null)}
                >
                  {link.name}
                </Link>
              ))}

              <div className="relative" onMouseEnter={() => openDropdown("about")}>
                <NavButton label={t("nav.aboutUs")} dropdownKey="about" />
              </div>
            </div>
          </div>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-2 relative z-10">
            <ThemeToggle theme={theme} onToggle={toggleTheme} variant={isDarkNav ? "navbar" : "navbar-light"} />
            <LanguageSwitcher variant={isDarkNav ? "navbar" : "navbar-light"} />
          </div>

          {/* Mobile hamburger */}
          <button
            className={cn(
              "lg:hidden p-2 rounded-xl transition-all duration-200",
              isDarkNav ? "text-white/70 hover:text-white hover:bg-white/10" : "text-foreground/70 hover:text-foreground hover:bg-secondary"
            )}
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? t("nav.closeMenu") : t("nav.openMenu")}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mega dropdown panels */}
      <AnimatePresence>
        {activeDropdown && (
          <motion.div
            className="hidden lg:block"
            onMouseEnter={() => openDropdown(activeDropdown)}
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="container mx-auto px-4 lg:px-8 pb-4">
              <div className={cn(
                "backdrop-blur-2xl border rounded-2xl shadow-2xl overflow-hidden relative",
                isDarkNav
                  ? "bg-[hsl(207,90%,10%)]/95 border-white/[0.08] shadow-black/30"
                  : "bg-card/95 border-border shadow-black/5"
              )}>
                {/* Glow accent */}
                <div className={cn(
                  "absolute top-0 left-1/2 -translate-x-1/2 w-48 h-px bg-gradient-to-r from-transparent to-transparent",
                  isDarkNav ? "via-[hsl(201,100%,50%)]/40" : "via-primary/30"
                )} />

                <div className="p-4">
                  {activeDropdown === "products" && (
                    <div className="grid grid-cols-2 gap-1">
                      {products.map((p) => renderDropdownItem(p))}
                    </div>
                  )}
                  {activeDropdown === "services" && (
                    <div className="max-w-md">
                      {services.map((s) => renderDropdownItem(s))}
                    </div>
                  )}
                  {activeDropdown === "crypto" && (
                    <div className="max-w-md">
                      {crypto.map((s) => renderDropdownItem(s))}
                    </div>
                  )}

                  {activeDropdown === "about" && (
                    <div className="grid grid-cols-2 xl:grid-cols-3 gap-1">
                      {aboutLinks.map((a) => renderDropdownItem(a))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              "lg:hidden overflow-hidden backdrop-blur-2xl border-b",
              isDarkNav
                ? "bg-[hsl(207,90%,8%)]/98 border-white/[0.06]"
                : "bg-background/98 border-border"
            )}
            style={{ maxHeight: "calc(100vh - 64px)" }}
          >
            <div className="container mx-auto px-4 py-6 space-y-1 overflow-y-auto" style={{ maxHeight: "calc(100vh - 64px)" }}>
              {/* Mobile theme + language controls */}
              <div className="flex items-center justify-end gap-2 pb-3 mb-2 border-b border-current/10">
                <ThemeToggle theme={theme} onToggle={toggleTheme} variant={isDarkNav ? "navbar" : "navbar-light"} />
                <LanguageSwitcher variant={isDarkNav ? "navbar" : "navbar-light"} />
              </div>
              <p className={cn("text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2", isDarkNav ? "text-white/30" : "text-muted-foreground/60")}>{t("nav.products")}</p>
              {products.map((product) => (
                <Link key={product.name} to={product.href} className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
                  isDarkNav ? "text-white/80 hover:text-white hover:bg-white/[0.05]" : "text-foreground/80 hover:text-foreground hover:bg-secondary"
                )} onClick={() => setIsOpen(false)}>
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", iconBg, iconColor)}>{product.icon}</div>
                  <div>
                    <div className="text-sm font-medium">{product.name}</div>
                    <div className={cn("text-xs", isDarkNav ? "text-white/30" : "text-muted-foreground")}>{product.description}</div>
                  </div>
                </Link>
              ))}

              <div className={cn("h-px my-4", dividerColor)} />

              <p className={cn("text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2", isDarkNav ? "text-white/30" : "text-muted-foreground/60")}>{t("services.label") || "Eficiencia Operativa"}</p>
              {services.map((service) => (
                <Link key={service.name} to={service.href} className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
                  isDarkNav ? "text-white/80 hover:text-white hover:bg-white/[0.05]" : "text-foreground/80 hover:text-foreground hover:bg-secondary"
                )} onClick={() => setIsOpen(false)}>
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", iconBg, iconColor)}>{service.icon}</div>
                  <div>
                    <div className="text-sm font-medium">{service.name}</div>
                    <div className={cn("text-xs", isDarkNav ? "text-white/30" : "text-muted-foreground")}>{service.description}</div>
                  </div>
                </Link>
              ))}

              <div className={cn("h-px my-4", dividerColor)} />

              <p className={cn("text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2", isDarkNav ? "text-white/30" : "text-muted-foreground/60")}>{t("crypto.label") || "Gobierno Criptográfico"}</p>
              {crypto.map((item) => (
                <Link key={item.name} to={item.href} className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
                  isDarkNav ? "text-white/80 hover:text-white hover:bg-white/[0.05]" : "text-foreground/80 hover:text-foreground hover:bg-secondary"
                )} onClick={() => setIsOpen(false)}>
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", iconBg, iconColor)}>{item.icon}</div>
                  <div>
                    <div className="text-sm font-medium">{item.name}</div>
                    <div className={cn("text-xs", isDarkNav ? "text-white/30" : "text-muted-foreground")}>{item.description}</div>
                  </div>
                </Link>
              ))}

              <div className={cn("h-px my-4", dividerColor)} />

              {navLinks.map((link) => (
                <Link key={link.name} to={link.href} className={cn(
                  "block px-4 py-3 text-sm font-medium rounded-xl transition-all",
                  isDarkNav ? "text-white/70 hover:text-white hover:bg-white/[0.05]" : "text-foreground/70 hover:text-foreground hover:bg-secondary"
                )} onClick={() => setIsOpen(false)}>
                  {link.name}
                </Link>
              ))}

              <div className={cn("h-px my-4", dividerColor)} />

              <p className={cn("text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2", isDarkNav ? "text-white/30" : "text-muted-foreground/60")}>{t("nav.aboutUs")}</p>
              {aboutLinks.map((item) => (
                <Link key={item.name} to={item.href} className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
                  isDarkNav ? "text-white/80 hover:text-white hover:bg-white/[0.05]" : "text-foreground/80 hover:text-foreground hover:bg-secondary"
                )} onClick={() => setIsOpen(false)}>
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", iconBg, iconColor)}>{item.icon}</div>
                  <div>
                    <div className="text-sm font-medium">{item.name}</div>
                    <div className={cn("text-xs", isDarkNav ? "text-white/30" : "text-muted-foreground")}>{item.description}</div>
                  </div>
                </Link>
              ))}

              <div className={cn("h-px my-4", dividerColor)} />
              <div className="px-4 py-2 flex items-center gap-3">
                <ThemeToggle theme={theme} onToggle={toggleTheme} variant={isDarkNav ? "mobile" : "mobile-light"} />
                <LanguageSwitcher variant={isDarkNav ? "navbar" : "navbar-light"} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
